import { CSSProperties, useCallback, useContext, useEffect, useRef } from 'react';
import { Paper } from '@mui/material';
import { DealerAnimationContext } from 'contexts/DealerAnimationContext';
import DealerChipContext from 'contexts/DealerChipContext';
import GlobalContext from 'contexts/GlobalContext';
import { useAppSelector } from 'hooks';
import { SeatPosition } from 'hooks/useEllipticalPositions';
import { useRegisterFloatingAnchor } from 'hooks/useFloatingRegistry';
import { useGuest } from 'hooks/useGuest';
import { useOrientation } from 'hooks/useOrientation';
import { useTableId, useTableSelector } from 'hooks/useTableSelector';
import { selectMySpotHasValidOffer } from 'reducers/waitlist.selectors';
import { consumeSeatIntent, markSeatIntentBeforeLogin, peekSeatIntent } from 'utils/auth';

import { promptLogin } from 'components/LoginPrompt/loginPromptStore';
import { DealerChip } from 'components/Seat/components';
import { getSeatDealerOffset } from 'components/Seat/styles';

import { useSeatAnimation } from './index';

type OpenSeatProps = {
  seatId: number;
  position: SeatPosition;
  isRotating?: boolean;
  /**
   * A seat on a hand-history snapshot: drawn exactly like an open seat, but no
   * tap, hover, waitlist state or post-login seat intent acts on it.
   */
  inert?: boolean;
};

export const OpenSeat = ({
  seatId,
  position,
  isRotating = false,
  inert = false
}: OpenSeatProps) => {
  const orientation = useOrientation();
  const isPortrait = orientation === 'portrait';
  const seatRef = useRef<HTMLDivElement>(null);
  const turnGlowRef = useRef<HTMLDivElement>(null);
  const opponentGlowRef = useRef<HTMLDivElement>(null);
  const dealerChipRef = useRef<HTMLDivElement>(null);

  const { isAnimating, dealer } = useContext(DealerAnimationContext);

  // Register this (empty) seat's dealer-chip anchor with the floating registry,
  // exactly like the occupied seat modes do. Without it, when a dead-button
  // rotation lands the dealer button on a VACATED seat (a player exited before
  // the next hand), the floating DealerButton can't resolve a position for the
  // now-open seat (refs.get(seatId) is undefined) and stays stuck on its
  // previous seat — misrepresenting the dead-dealer rotation. See DealerButton.
  useRegisterFloatingAnchor(DealerChipContext, seatId, dealerChipRef);

  const isSeated = useTableSelector((table) => table.currentPlayer.isSeated);
  const { setOpenAskBuyin, setManuallyAssignedSeatId, setOpenAskPassword, setAskBuyinTable } =
    useContext(GlobalContext);
  const isShowdown = useTableSelector((table) => table.gameState.isShowdown);
  const { waitlistCount, privateTable, dealerVisible, gameID, game } = useTableSelector(
    (table) => table.game
  );
  const contextTableId = useTableId();
  // Canonical eligibility — guards against stale-socket, expired window,
  // and no-available-seats cases where `yourTurn` might still be true in
  // the local slice.
  const hasValidOffer = useAppSelector(selectMySpotHasValidOffer(Number(gameID))) && !inert;
  const isMyTurn = hasValidOffer;
  // The seat is being held for the queue as far as THIS viewer is concerned.
  // Exactly the condition internalJoinTable already refuses on, so the label and
  // the affordances now agree with it: the seat used to read "Open", glow on
  // hover and show a pointer cursor while every tap was silently swallowed.
  //
  // Note this is deliberately "a queue exists", not "an offer is counting down".
  // Other players receive NO waitlist rows at all (the broadcaster groups by
  // playerGuid and emits each player only their own — socket-broadcast-worker
  // broadcastPlayerWaitlist), and the table payload carries only waitlistCount.
  // So a live-offer countdown cannot be shown here without a backend change; the
  // seat is blocked for the whole time a queue exists anyway, which is the
  // longer and more useful truth to tell.
  const heldForWaitlist = !inert && waitlistCount > 0 && !hasValidOffer;
  const guest = useGuest();
  // Guests always get a tap (it routes to login), so they keep the affordance.
  const interactive = !inert && (guest || (!isSeated && !heldForWaitlist));
  const seatLabel = isMyTurn ? 'Your Seat' : heldForWaitlist ? 'Reserved' : 'Open';
  // Labels that are a single word too long for the badge at the shared size —
  // they get fitted rather than wrapped (see the span below).
  const fitsOnOneLine = seatLabel === 'Reserved';
  // See the post-login auto-effect below: isSeated is only trustworthy once
  // this identity signal resolves.
  const playerGuid = useAppSelector((state) => state.user.playerGUID) || '';

  const showDealerChip = !!dealerVisible && seatId === dealer && !isAnimating;

  useSeatAnimation(false, false, isPortrait, seatRef, turnGlowRef, opponentGlowRef);

  const internalJoinTable = useCallback(() => {
    // Guests observe only — clicking an open seat advises them to log in / sign
    // up rather than opening the buy-in flow (which the server would reject).
    if (guest) {
      markSeatIntentBeforeLogin(String(gameID), seatId);
      promptLogin();
      return;
    }
    if (isSeated) return;
    // Block only when a waitlist is active AND the player is not being
    // offered this seat (via the validated eligibility selector, so an
    // expired/stale offer does not let them through).
    if (waitlistCount > 0 && !hasValidOffer) return;
    // Bind which table this seat belongs to before any modal opens — see
    // GlobalContext.askBuyinTable (the shared-modal stacking RCA, 8/25).
    setAskBuyinTable({ gameID: String(contextTableId || gameID), game: game || '' });
    if (privateTable) {
      setOpenAskPassword(true);
    } else {
      setOpenAskBuyin(true);
    }
    setManuallyAssignedSeatId(seatId);
    // contextTableId/game/setAskBuyinTable are read by the bind above and must
    // be declared. They happened to be fresh only because privateTable and
    // waitlistCount come off the same table.game object, so the callback was
    // recreated when the slice hydrated and picked `game` up incidentally —
    // coincidence, not correctness, and `game` is what the type-routing fix
    // depends on. Narrow those deps for perf and the closure would silently
    // pin `game: ''` (JRam review, #3721).
  }, [
    guest,
    isSeated,
    seatId,
    gameID,
    contextTableId,
    game,
    setAskBuyinTable,
    setManuallyAssignedSeatId,
    setOpenAskBuyin,
    setOpenAskPassword,
    privateTable,
    waitlistCount,
    hasValidOffer
  ]);

  // A guest who clicked THIS seat gets sent (via the advisory modal) to login
  // with the seat stashed (markSeatIntentBeforeLogin above). utils/auth's
  // consumeLoginReturnPath then hard-reloads them back to this table. Finish
  // opening the buy-in/password flow for the same seat once authenticated,
  // instead of making them find and click it again.
  //
  // playerGuid and gameID both gate: `guest` flips false the instant the
  // reload sees a valid auth token — synchronous, no round trip — but
  // isSeated/waitlistCount/hasValidOffer only become trustworthy once BOTH
  // the legacy engine's GOT_LOGIN response (resolves identity) and the
  // table-data socket/EventBridge snapshot (resolves capacity) land. Those
  // are two independent, unordered connections, so deciding the instant they
  // FIRST look ready made this intermittent — whichever socket happened to be
  // slower on a given load left the OTHER one's data stale at that render.
  //
  // So this debounces instead of deciding once: every render where the
  // intent still matches this gameID+seatId reschedules the decision; only
  // once every relevant field has stopped changing for a full quiet window
  // does it commit (consume the one-shot stash + act). peekSeatIntent (unlike
  // consumeSeatIntent) never clears the stash, so an in-flight debounce can't
  // lose the intent before it's ready to commit.
  useEffect(() => {
    if (inert) return;
    if (guest) return;
    if (!playerGuid) return;
    if (!peekSeatIntent(String(gameID), seatId)) return;

    const timer = setTimeout(() => {
      if (isSeated) return;
      if (!consumeSeatIntent(String(gameID), seatId)) return;
      if (waitlistCount > 0 && !hasValidOffer) return;
      setAskBuyinTable({ gameID: String(contextTableId || gameID), game: game || '' });
      if (privateTable) {
        setOpenAskPassword(true);
      } else {
        setOpenAskBuyin(true);
      }
      setManuallyAssignedSeatId(seatId);
    }, 400);

    return () => clearTimeout(timer);
    // contextTableId/game and the setters are read when the timer fires, so a
    // change to any of them reschedules it — otherwise a variant that hydrates
    // mid-debounce is bound as the empty one the timer closed over.
  }, [
    inert,
    guest,
    gameID,
    contextTableId,
    game,
    playerGuid,
    isSeated,
    waitlistCount,
    hasValidOffer,
    privateTable,
    seatId,
    setAskBuyinTable,
    setManuallyAssignedSeatId,
    setOpenAskBuyin,
    setOpenAskPassword
  ]);

  const openSeatContainerStyle: CSSProperties = {
    position: 'absolute',
    left: `${position.leftPct}%`,
    top: `${position.topPct}%`,
    // The open seat scales with the table (0.7 × --seat-scale) but is clamped to
    // a minimum so the whole badge — circle AND label — never shrinks past a
    // usable, readable size on small screens (below that the floored text used
    // to overflow the circle). The label uses a plain font that scales with this
    // clamped badge, so it stays readable and fits. seatRef is only GSAP-animated
    // on a win (never for open seats), so this transform is safe.
    // Size = max(floor, factor × --seat-scale): the factor sets the size on big
    // screens (--seat-scale = 1), the floor sets the smallest it shrinks to on
    // small screens (kept ≥ ~0.45 so the "Open" label stays legible). Landscape
    // is a touch smaller than portrait.
    transform: isPortrait
      ? 'translate(-50%, -50%) scale(max(0.52, calc(0.85 * var(--seat-scale, 1))))'
      : 'translate(-50%, -50%) scale(max(0.52, calc(0.8 * var(--seat-scale, 1))))',
    transformOrigin: 'center center',
    // Off during the POV seat-assignment rotation (positions are stepped along
    // the arc every frame), on for the usual single position changes.
    transition: isRotating ? 'none' : 'left 0.5s ease, top 0.5s ease',
    zIndex: 3,
    overflow: 'visible'
  };

  // "Your seat is ready" overrides the showdown/default look with an orange
  // accent so the offered seat is unmistakable; without an offer the seat
  // falls back to its showdown-dim or default-dashed appearance.
  const openSeatStyle: CSSProperties = {
    display: 'flex',
    width: 56,
    height: 56,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '100%',
    transition: 'all 0.25s ease',
    boxSizing: 'border-box',
    cursor: interactive ? 'pointer' : 'default',
    zIndex: 5,
    position: 'relative',
    ...(isMyTurn
      ? {
          opacity: 1,
          backgroundColor: 'rgba(233, 140, 38, 0.15)',
          border: '2px solid #E98C26',
          boxShadow: '0 0 12px rgba(233, 140, 38, 0.6)'
        }
      : isShowdown
      ? {
          opacity: 0.8,
          backgroundColor: '#000000'
        }
      : {
          opacity: 1,
          backgroundColor: 'rgba(30, 45, 60, 0.8)',
          border: '2px dashed rgba(255,255,255,0.2)'
        })
  };

  const restingBorder = isMyTurn ? '2px solid #E98C26' : '2px dashed rgba(255,255,255,0.2)';
  const restingShadow = isMyTurn ? '0 0 12px rgba(233, 140, 38, 0.6)' : 'none';

  // Same inside-the-seat, toward-the-centre rule every seated player uses, so
  // the button's travel reads consistently as it passes an empty seat.
  const dealerChipPosition = getSeatDealerOffset(position, isPortrait);

  return (
    // Outer wrapper owns the responsive scale; the inner seatRef is the element
    // useSeatAnimation's fade-in scales (scale 0.85→1) on mount — keeping that
    // off the wrapper so it doesn't overwrite the responsive --seat-scale.
    <div style={openSeatContainerStyle}>
      <div ref={seatRef} style={{ position: 'relative', overflow: 'visible' }}>
        <Paper
          elevation={0}
          aria-label={isMyTurn ? 'your-seat' : heldForWaitlist ? 'reserved-seat' : 'open-seat'}
          style={openSeatStyle}
          onMouseEnter={(e) => {
            if (interactive) {
              (e.currentTarget as HTMLElement).style.borderColor = isMyTurn
                ? '#E98C26'
                : 'rgba(233, 140, 38, 0.5)';
              (e.currentTarget as HTMLElement).style.boxShadow = isMyTurn
                ? '0 0 18px rgba(233, 140, 38, 0.85)'
                : '0 0 12px rgba(233, 140, 38, 0.3)';
              (e.currentTarget as HTMLElement).style.transform = 'scale(1.05)';
            }
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.borderColor = restingBorder;
            (e.currentTarget as HTMLElement).style.boxShadow = restingShadow;
            (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
          }}
          onClick={() => {
            if (!inert) internalJoinTable();
          }}>
          <span
            style={{
              fontFamily: 'Outfit',
              // Plain size — scales with the clamped badge above. The badge's
              // minimum keeps this legible; this design size lands ~8–10px rendered.
              // "Reserved" is the one label that cannot use it: it is a single
              // unbreakable word, so unlike "Your Seat" (which wraps to two
              // lines) it renders wider than the 56px badge and spills outside
              // the circle. Fit that one instead of letting it overhang.
              fontSize: fitsOnOneLine ? '0.72rem' : '1rem',
              letterSpacing: fitsOnOneLine ? '0.2px' : '0.5px',
              width: fitsOnOneLine ? '54px' : '50px',
              whiteSpace: fitsOnOneLine ? 'nowrap' : undefined,
              textAlign: 'center',
              color: isMyTurn ? '#E98C26' : 'rgba(255, 255, 255, 0.7)',
              fontWeight: isMyTurn ? 700 : 500,
              userSelect: isSeated ? 'none' : 'inherit'
            }}>
            {seatLabel}
          </span>
        </Paper>

        {/* Dealer chip positioning reference for OuterDealerButton. Offset,
            size and layering all come from getSeatDealerOffset, so the button
            passing over an EMPTY seat reads identically to an occupied one. */}
        <div
          ref={dealerChipRef}
          style={{
            ...dealerChipPosition,
            opacity: showDealerChip ? 1 : 0,
            pointerEvents: showDealerChip ? 'auto' : 'none'
          }}>
          <DealerChip isPortrait={isPortrait} />
        </div>
      </div>
    </div>
  );
};
